'use client';

import { Button } from '@florencelabs/button';
import { Collapse } from '@florencelabs/collapse';
import { MaterialSymbol } from '@florencelabs/icons';
import clsx from 'clsx';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ProviderOrganizationTitlesProps {
  titles: string[];
  qaIdPrefix: string;
}

const DESKTOP_LIMIT = 5;
const MOBILE_LIMIT = 2;

export const ProviderOrganizationTitles = ({
  titles,
  qaIdPrefix,
}: ProviderOrganizationTitlesProps): ReactElement | null => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  if (titles.length === 0) {
    return null;
  }

  const desktopTitles = titles.slice(0, DESKTOP_LIMIT);
  const initialMobileTitles = titles.slice(0, MOBILE_LIMIT);
  const additionalMobileTitles = titles.slice(MOBILE_LIMIT);
  const showMobileToggle = titles.length > MOBILE_LIMIT;

  return (
    <>
      {/* Desktop view: First 5 titles, no expand */}
      <div
        data-testid={`${qaIdPrefix}-titles`}
        className={
          'text-body-broad-medium-semibold col-span-1 hidden flex-col gap-1 desktop:col-span-2 desktop:flex'
        }
      >
        {desktopTitles.map((title, index) => (
          <span itemProp={'name'} key={`${title}-${index}`}>
            {title}
          </span>
        ))}
      </div>

      {/* Mobile view: 2 titles with expand/collapse */}
      <div
        data-testid={`${qaIdPrefix}-titles`}
        className={
          'text-body-broad-medium-semibold col-span-1 flex flex-col gap-1 desktop:hidden'
        }
      >
        {initialMobileTitles.map((title, index) => (
          <span itemProp={'name'} key={`${title}-${index}`}>
            {title}
          </span>
        ))}

        {showMobileToggle && (
          <>
            <Collapse open={isExpanded}>
              <div className={'flex flex-col gap-1 pt-1'}>
                {additionalMobileTitles.map((title, index) => (
                  <span
                    itemProp={'name'}
                    key={`${title}-${index + MOBILE_LIMIT}`}
                  >
                    {title}
                  </span>
                ))}
              </div>
            </Collapse>
            <Button
              variant={'systemLink'}
              onClick={() => {
                setIsExpanded(!isExpanded);
              }}
              className={
                'mt-2 flex items-center gap-1 self-start text-body-small-semibold'
              }
              aria-expanded={isExpanded}
              aria-label={
                isExpanded
                  ? t('details.showLessTitles')
                  : t('details.showMoreTitles')
              }
              testId={`${qaIdPrefix}-titles-toggle`}
            >
              <span className={'text-primary-color'}>
                {isExpanded
                  ? t('details.showLessTitles')
                  : t('details.showMoreTitles')}
              </span>
              <MaterialSymbol
                name={'keyboard_arrow_up'}
                size={16}
                aria-hidden={'true'}
                className={clsx('transition-transform duration-300', {
                  'rotate-180': !isExpanded,
                })}
              />
            </Button>
          </>
        )}
      </div>
    </>
  );
};
